import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { SUPPORT_EMAIL } from "@/i18n/content";
import { Reveal } from "@/components/Reveal";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Marquee } from "@/components/Marquee";
import { ContactForm } from "@/components/ContactForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowDown, Mail, ShieldCheck, RefreshCw, Wifi, LogIn, Bell, Download } from "lucide-react";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const stepIcons = [Download, Wifi, RefreshCw, LogIn, Bell];

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPillars = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-background" data-testid="hero-section">
      <motion.div
        style={{ y: yPillars }}
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center gap-3 opacity-[0.07] sm:gap-5"
      >
        {[38, 62, 88, 55, 74, 46].map((h, i) => (
          <div key={i} className="w-10 rounded-t-lg bg-primary sm:w-16" style={{ height: `${h}vh` }} />
        ))}
      </motion.div>

      <motion.div style={{ y: yText }} className="relative mx-auto max-w-6xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-accent px-4 py-1.5 text-xs font-bold tracking-wide text-primary"
          data-testid="hero-badge"
        >
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t.hero.badge}
        </motion.p>

        <h1 className="text-balance text-4xl font-extrabold leading-[1.15] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="hero-title">
          <span className="mask-line"><motion.span className="block" {...lineAnim(0)}>{t.hero.line1}</motion.span></span>
          <span className="mask-line"><motion.span className="block text-primary" {...lineAnim(1)}>{t.hero.line2}</motion.span></span>
          <span className="mask-line"><motion.span className="block text-2xl font-bold leading-snug text-muted-foreground sm:text-3xl lg:text-4xl" {...lineAnim(2)}>{t.hero.line3}</motion.span></span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#getting-help"
            data-testid="hero-get-help-button"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-bold text-primary-foreground shadow-md transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg"
          >
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
            {t.hero.ctaHelp}
          </a>
          <Link
            to="/privacy"
            data-testid="hero-privacy-link"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-7 py-3.5 text-base font-bold text-foreground shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            {t.hero.ctaPrivacy}
          </Link>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-sm font-semibold text-muted-foreground"
          data-testid="hero-meta-list"
        >
          {t.hero.meta.map((m) => (
            <li key={m} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
              {m}
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
};

const SupportPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = "Al Maarifah Houses Support | دعم بيوت المعرفة";
  }, []);

  return (
    <main data-testid="support-page">
      <Hero />
      <Marquee items={t.marquee} />

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24" aria-labelledby="about-app" data-testid="about-section">
        <Reveal>
          <ChapterHeading number={t.about.chapter} title={t.about.title} />
        </Reveal>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{t.about.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{t.about.p2}</p>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {t.about.pillars.map((p, i) => (
              <Reveal key={p.title} delay={0.12 + i * 0.08}>
                <div className="group h-full rounded-lg border border-border bg-card p-6 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`pillar-card-${i}`}>
                  <span className="text-2xl font-extrabold text-primary/30">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-lg font-bold text-foreground">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="getting-help" className="border-y border-border bg-white" aria-labelledby="getting-help" data-testid="getting-help-section">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <ChapterHeading number={t.help.chapter} title={t.help.title} intro={t.help.intro} />
          </Reveal>
          <ol className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="help-steps-list">
            {t.help.steps.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <Reveal key={i} delay={i * 0.07}>
                  <li className="flex h-full gap-4 rounded-lg border border-border bg-background p-6 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`help-step-${i + 1}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <span className="text-xs font-bold tracking-widest text-primary/60">{i + 1}</span>
                      <p className="mt-1 text-sm font-medium leading-relaxed text-foreground">{step}</p>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24" aria-labelledby="faq" data-testid="faq-section">
        <Reveal>
          <ChapterHeading number={t.faq.chapter} title={t.faq.title} />
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="rounded-lg border border-border bg-card px-6 shadow-sm" data-testid="faq-accordion">
            {t.faq.items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`}>
                <AccordionTrigger className="text-start text-base font-bold text-foreground hover:no-underline" data-testid={`faq-question-${i + 1}`}>
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground" data-testid={`faq-answer-${i + 1}`}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="border-t border-border bg-white" aria-labelledby="contact" data-testid="contact-section">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <ChapterHeading number={t.contact.chapter} title={t.contact.title} />
          </Reveal>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="rounded-lg border border-primary/20 bg-accent/50 p-8">
                <p className="text-sm font-bold tracking-wide text-primary">{t.contact.emailLabel}</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  dir="ltr"
                  data-testid="support-email-link"
                  className="mt-3 inline-flex items-center gap-2 break-all text-lg font-extrabold text-foreground underline-offset-4 transition-colors duration-200 hover:text-primary hover:underline"
                >
                  <Mail className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  {SUPPORT_EMAIL}
                </a>
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground" data-testid="contact-note">{t.contact.note}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-3">
              <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
                <h3 className="mb-6 text-xl font-bold text-foreground">{t.contact.formTitle}</h3>
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SupportPage;
